import {
  ISharedInvoicePaymentReq,
  ISharedInvoicePaymentUI,
} from '@monetize/types/app';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useAtom } from 'jotai';
import { handleApiErrorToast } from '../../api/axios';
import { useInvoicePaymentForInvoiceShare } from '../../api/shareService';
import { getInvoiceShareStripePaymentCallbackRoute } from '../../constants/routes';
import { tempSharedInvoicePaymentState } from '../../store/global.store';

interface usePayInvoiceWithStripeAnonymousProps {
  tenantId: string;
  invoiceSecretId: string;
  allowPartialPayment: boolean;
  amountDue: number;
  onSuccess: () => void;
}

export const usePayInvoiceWithStripeAnonymous = ({
  tenantId,
  invoiceSecretId,
  allowPartialPayment,
  amountDue,
  onSuccess,
}: usePayInvoiceWithStripeAnonymousProps) => {
  const elements = useElements();
  const stripe = useStripe();

  const [requiresVerification, setRequiredVerification] = useState<
    false | true | { verificationUrl: string }
  >(false);

  const [sharedInvoicePayment, setSharedInvoicePayment] = useAtom(
    tempSharedInvoicePaymentState,
  );

  const {
    mutateAsync: doCreateSharedInvoicePayment,
    isPending: isSavePaymentLoading,
  } = useInvoicePaymentForInvoiceShare();

  const { mutateAsync: confirmSetupStripePayment, isPending: stripeLoading } =
    useMutation({
      mutationFn: ({
        stripeInstance,
        elementsInstance,
        billingDetails,
      }: {
        stripeInstance: Stripe;
        elementsInstance: StripeElements;
        billingDetails: { email: string; name: string };
      }) =>
        stripeInstance.confirmSetup({
          elements: elementsInstance,
          confirmParams: {
            return_url: getStripeCallbackUrl(),
            payment_method_data: {
              billing_details: billingDetails,
            },
          },
          redirect: 'if_required',
        }),
    });

  const getStripeCallbackUrl = () => {
    const url = getInvoiceShareStripePaymentCallbackRoute(
      tenantId,
      invoiceSecretId,
    );
    return `${window.location.origin}${url}`;
  };

  const handleFormSubmit = async (requestData: ISharedInvoicePaymentUI) => {
    try {
      if (!stripe || !elements) {
        return;
      }
      // Store in localstorage so we can get data back if Stripe invokes the callback
      setSharedInvoicePayment(requestData);

      const { email, fullName } = requestData.billingDetails;
      /**
       * Confirm payment method with Stripe
       */
      const response = await confirmSetupStripePayment({
        stripeInstance: stripe,
        elementsInstance: elements,
        billingDetails: { email, name: fullName },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { setupIntent } = response;

      if (
        setupIntent.status === 'requires_action' &&
        setupIntent?.next_action
      ) {
        if (
          setupIntent.next_action.type === 'redirect_to_url' &&
          setupIntent.next_action.redirect_to_url?.url
        ) {
          window.location.href = setupIntent.next_action.redirect_to_url.url;
        } else if (
          setupIntent.next_action.type === 'verify_with_microdeposits'
        ) {
          const verificationUrl =
            setupIntent.next_action.verify_with_microdeposits
              ?.hosted_verification_url;

          setRequiredVerification(verificationUrl ? { verificationUrl } : true);
        }
      } else if (
        (setupIntent.status === 'succeeded' ||
          setupIntent.status === 'processing') &&
        setupIntent?.id
      ) {
        await createInvoicePayment(setupIntent.id, requestData);
      }
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  const createInvoicePayment = async (
    paymentToken: string,
    requestData: ISharedInvoicePaymentUI,
  ) => {
    try {
      const amount =
        allowPartialPayment && sharedInvoicePayment?.amount
          ? sharedInvoicePayment.amount
          : amountDue;

      const payload: ISharedInvoicePaymentReq = {
        amount,
        paymentToken,
        paymentMethodName: requestData.billingDetails.paymentMethodName,
        billingDetails: {
          fullName: requestData.billingDetails.fullName,
          email: requestData.billingDetails.email,
        },
      };
      await doCreateSharedInvoicePayment({
        invoiceSecretId,
        tenantId,
        body: payload,
      });
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      setSharedInvoicePayment(null);
      onSuccess();
    }
  };

  return {
    elements,
    stripe,
    requiresVerification,
    isSavePaymentLoading,
    stripeLoading,
    handleFormSubmit,
  };
};
